import React from 'react';
import { ArrowRight, Download, Github, Linkedin, Mail } from 'lucide-react';
import { personalInfo } from '../data/mock';

const Hero = () => {
  const scrollToProjects = () => {
    const element = document.querySelector('#projects');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const scrollToContact = () => {
    const element = document.querySelector('#contact');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section className="min-h-screen bg-gradient-to-br from-[rgb(17,17,19)] via-[rgb(12,12,14)] to-[rgb(20,20,22)] flex items-center justify-center pt-16 relative overflow-hidden">
      {/* Glassmorphism background orbs */}
      <div className="glass-orb glass-orb-1"></div>
      <div className="glass-orb glass-orb-2"></div>
      
      {/* Additional floating glass elements */}
      <div className="absolute top-1/4 left-1/4 w-32 h-32 rounded-full bg-gradient-to-br from-[rgba(218,255,1,0.1)] to-transparent backdrop-blur-sm opacity-60 animate-pulse"></div>
      <div className="absolute bottom-1/3 right-1/3 w-24 h-24 rounded-full bg-gradient-to-tl from-[rgba(218,255,1,0.08)] to-transparent backdrop-blur-sm opacity-40 animate-pulse" style={{animationDelay: '1s'}}></div>

      <div className="container mx-auto px-6 relative z-10">
        <div className="max-w-4xl mx-auto text-center">
          {/* Main content with enhanced glass effects */}
          <div className="space-y-8 animate-fade-in">
            {/* Greeting with glass accent */}
            <div className="space-y-2">
              <div className="inline-block glass-light px-6 py-2 rounded-full mb-4">
                <p className="text-[rgb(218,255,1)] text-lg font-medium tracking-wide">
                  Hello, I'm
                </p>
              </div>
              <h1 className="text-5xl md:text-7xl font-bold text-white mb-4 relative">
                <span className="relative z-10">
                  Arjun <span className="text-[rgb(218,255,1)] relative">
                    Bhusal
                    <div className="absolute inset-0 bg-[rgb(218,255,1)] opacity-20 blur-xl rounded-lg"></div>
                  </span>
                </span>
              </h1>
              <h2 className="text-2xl md:text-3xl font-semibold text-[rgb(218,218,218)] mb-6">
                {personalInfo.title}
              </h2>
            </div>

            {/* Bio with glass background */}
            <div className="glass-card rounded-2xl p-8 max-w-3xl mx-auto">
              <p className="text-lg text-[rgb(161,161,170)] leading-relaxed">
                {personalInfo.bio}
              </p>
            </div>

            {/* CTA Buttons with enhanced glassmorphism */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center pt-8">
              <button
                onClick={scrollToProjects}
                className="group glass-button text-[rgb(17,17,19)] px-8 py-4 rounded-xl font-semibold text-lg transition-all duration-300 hover:transform hover:translate-y-[-2px] hover:shadow-[0_12px_30px_rgba(218,255,1,0.4)] flex items-center gap-2 min-w-[200px] justify-center relative overflow-hidden"
              >
                <span className="relative z-10">View My Work</span>
                <ArrowRight size={20} className="group-hover:translate-x-1 transition-transform duration-300 relative z-10" />
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -skew-x-12 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-700"></div>
              </button>
              
              <button
                onClick={scrollToContact}
                className="glass-card hover:glass-card text-white hover:text-[rgb(218,255,1)] px-8 py-4 rounded-xl font-semibold text-lg transition-all duration-300 hover:transform hover:translate-y-[-2px] flex items-center gap-2 min-w-[200px] justify-center hover:border-[rgba(218,255,1,0.4)]"
              >
                <Download size={20} />
                Get In Touch
              </button>
            </div>

            {/* Quick Stats with glassmorphism */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 pt-16 max-w-2xl mx-auto">
              {[
                { value: "5+", label: "ML Projects" },
                { value: "4", label: "Certifications" },
                { value: "95%", label: "Max Accuracy" },
                { value: "2+", label: "Years Exp" }
              ].map((stat, index) => (
                <div key={index} className="glass-card rounded-2xl p-6 text-center hover:glass-card transition-all duration-300 hover:transform hover:scale-105">
                  <div className="text-3xl font-bold text-[rgb(218,255,1)] mb-2 relative">
                    {stat.value}
                    <div className="absolute inset-0 bg-[rgb(218,255,1)] opacity-10 blur-md rounded-lg"></div>
                  </div>
                  <div className="text-sm text-[rgb(161,161,170)]">{stat.label}</div>
                </div>
              ))}
            </div>

            {/* Social Links with enhanced glass effect */}
            <div className="flex justify-center items-center space-x-6 pt-12">
              {[
                { href: personalInfo.github, icon: Github },
                { href: personalInfo.linkedin, icon: Linkedin },
                { href: `mailto:${personalInfo.email}`, icon: Mail }
              ].map((social, index) => {
                const Icon = social.icon;
                return (
                  <a
                    key={index}
                    href={social.href}
                    target={social.href.startsWith('mailto:') ? undefined : "_blank"}
                    rel={social.href.startsWith('mailto:') ? undefined : "noopener noreferrer"}
                    className="glass-card w-14 h-14 rounded-2xl flex items-center justify-center text-[rgb(161,161,170)] hover:text-[rgb(218,255,1)] hover:border-[rgba(218,255,1,0.4)] hover:transform hover:translate-y-[-4px] hover:shadow-[0_8px_32px_rgba(218,255,1,0.2)] transition-all duration-300 group"
                  >
                    <Icon size={24} className="group-hover:scale-110 transition-transform duration-300" />
                  </a>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* Enhanced scroll indicator with glass effect */}
      <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
        <div className="glass-light w-8 h-12 rounded-full flex justify-center border-2 border-[rgba(218,255,1,0.3)]">
          <div className="w-1 h-3 bg-[rgb(218,255,1)] rounded-full mt-2 animate-pulse shadow-[0_0_10px_rgba(218,255,1,0.5)]"></div>
        </div>
      </div>
    </section>
  );
};

export default Hero;